/**
 * 63.1t, scouts ride (GameConfig.scoutRide, on unless false; the owner: *"So maybe 'scout by vehicle' just needs to be an
 * option?"* and *"Also for vehicle scouting it should be prefer motorcycle -> technical -> troop transport -> armor (tank,
 * etc)"*). A squad the commander sends to scout (69.5a: a `move` to a circle its side knows only roughly) goes by the first
 * vehicle of SCOUT_VEHICLES it can have, the squad kept together:
 *
 * - **motorcycle:** a free team bike within `bikeReach` for every living member, one each (a bike carries its rider) — the
 *   squad rides as many bikes (the bot's own bike rule, X31, with its scout);
 * - **technical:** else a free technical (nobody aboard) within `techReach` of a member — its crew (63.1r, the technical
 *   loop) drives to its post over the circle, where the gun's eye resolves it;
 * - **wheeled:** else the side's 6×6 by the APC loop (63.1l) toward the scout's place;
 * - **armour** has no body yet: it takes its place in the list when it does.
 *
 * Read pure off the state, the same for every reader in a tick: the APC loop, the technical loop and the bot's bike rule ask it.
 */
import type { GameState, PlayerState, SquadOrder, VehicleKind } from '../types';
import { arcDistance } from '../core/gameState';
import { vehiclesBarred } from '../core/vehicleUse';
import { squadOf, squadTaskFor } from '../core/squads';

/** How far a scout looks for its vehicle (people): a bike within the bike rule's walk (P.bike.walk's default), a technical within the technical loop's crew reach (TECH_CREW_PEOPLE). */
export const SCOUT_REACH = { bikePeople: 10, techPeople: 60 } as const;

/** The order a scout takes a vehicle in (the owner's): the first it can have. */
export const SCOUT_VEHICLES = ['motorcycle', 'technical', 'wheeled'] as const satisfies readonly VehicleKind[];
export type ScoutVehicle = (typeof SCOUT_VEHICLES)[number];

/** The commander's scout in a task (69.5a: a `move` with `scout`, the commander's), or null. */
export function scoutOf(task: { order: SquadOrder; by?: string } | null): Extract<SquadOrder, { kind: 'move' }> | null {
  return task && task.by === 'commander' && task.order.kind === 'move' && task.order.scout ? task.order : null;
}

/**
 * The vehicle a scouting squad (its living members, on one planet) goes by, by SCOUT_VEHICLES' order — null with scouts
 * riding off, the side kept on foot (68.25), or none of the ranks to hand (the 6×6 is the APC loop's own call: `wheeled` here
 * means "neither a bike each nor a free technical").
 */
export function scoutVehicle(state: GameState, members: readonly PlayerState[], tick: number, bikeReach: number, techReach: number): ScoutVehicle | null {
  if (state.config.scoutRide === false || !members.length) return null;
  const team = members[0].teamIndex, pi = members[0].planetIndex;
  if (vehiclesBarred(state, team) || members.some(p => p.planetIndex !== pi)) return null;
  const R = state.planets[pi].radius;
  const ours = (s: { ownerId: number }) => state.players.find(p => p.id === s.ownerId)?.teamIndex === team;
  const free = (s: GameState['structures'][number]) => s.health > 0 && s.planetIndex === pi && ours(s) && !(s.seats ?? []).some(x => x.occupant);
  // a bike each, nearest first (members in id order: the same answer for every asker)
  const bikes = state.structures.filter(s => s.type === 'motorcycle' && free(s));
  const used = new Set<number>();
  let allBiked = bikes.length >= members.length;
  for (const p of [...members].sort((a, b) => a.id - b.id)) {
    if (!allBiked) break;
    let best: number | null = null, bestD = Infinity;
    for (const s of bikes) {
      if (used.has(s.id)) continue;
      const d = arcDistance(s.surfaceDir, p.surfaceDir, R);
      if (d <= bikeReach && (d < bestD || (d === bestD && best !== null && s.id < best))) { best = s.id; bestD = d; }
    }
    if (best === null) allBiked = false; else used.add(best);
  }
  if (allBiked) return 'motorcycle';
  // a free technical this squad is the nearest scouting squad to (one squad a technical — the others go on to the 6×6)
  const near = (ms: readonly PlayerState[], s: { surfaceDir: GameState['structures'][number]['surfaceDir'] }) => Math.min(...ms.map(p => arcDistance(s.surfaceDir, p.surfaceDir, R)));
  const mine = squadOf(state, members[0].id)?.id;
  const scouting = (state.squads ?? []).filter(q => q.team === team).map(q => ({ id: q.id, ms: q.unitIds.map(id => state.players.find(p => p.id === id)).filter((p): p is PlayerState => !!p && p.alive && !p.mount && p.planetIndex === pi) }))
    .filter(q => q.ms.length > 0 && q.ms.every(p => scoutOf(squadTaskFor(state, p.id, tick)) !== null));
  for (const s of state.structures) {
    if (s.type !== 'technical' || !free(s)) continue;
    const first = scouting.filter(q => near(q.ms, s) <= techReach).sort((a, b) => near(a.ms, s) - near(b.ms, s) || a.id - b.id)[0];
    if (first && first.id === mine) return 'technical';
  }
  return 'wheeled';
}
